import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getColorHex } from './taskTypeColors';
import AutoComplete from './AutoComplete';
import FieldControl from './FieldControl';
import Icon from './Icon';

const Option = styled.span`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
`;

function ProjectTaskTypeSelect({ name, value, initialValue, mode, useDefault = false, onChange, ...props }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectTaskTypes()
        .get({ q, isActive: true, mode, include: initialValue ? initialValue.id : undefined });

      return data;
    },
    [workspace.id, initialValue, mode, api],
  );

  const refreshDefaultOptions = useCallback(async () => {
    const data = await handleSearch('');
    if (!isMounted.current) return;
    setDefaultOptions(data);
  }, [handleSearch, isMounted]);

  useEffect(() => {
    refreshDefaultOptions();
  }, [refreshDefaultOptions]);

  useEffect(() => {
    if (useDefault && !value && defaultOptions.length > 0) {
      const defaultOption = defaultOptions.find((opt) => opt.isDefault);
      if (defaultOption && _.isFunction(onChange)) {
        onChange({ target: { name, value: defaultOption } });
      }
    }
  }, [useDefault, defaultOptions, name, value, onChange]);

  const renderOption = (option) => (
    <Option>
      <IconWrapper>
        <Icon icon={option.icon} color={getColorHex(option.color)} />
      </IconWrapper>
      {option.name}
    </Option>
  );

  return (
    <AutoComplete
      name={name}
      onSearch={handleSearch}
      value={value}
      onChange={onChange}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      renderOption={renderOption}
      {...props}>
      {() => <AutoComplete.Results />}
    </AutoComplete>
  );
}

function FieldProjectTaskTypeSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ProjectTaskTypeSelect {...field} {...props} />
    </FieldControl>
  );
}

export default ProjectTaskTypeSelect;
export { FieldProjectTaskTypeSelect };
